// mapboxgl namespace imported for type references only (mapboxgl.Map, mapboxgl.ImageSource)
import type * as mapboxgl from 'mapbox-gl';
import type { EventWithCircuit } from '../../api/eventsService';
import type { Dayjs } from 'dayjs';

interface RadarTile {
  timestamp: string;
  url: string;
  datetime: Dayjs;
  isNowcast?: boolean;
  forecastOffset?: number;
}

interface UpdateRadarLayerParams {
  map: mapboxgl.Map;
  currentTile: RadarTile;
  selectedEvent: EventWithCircuit | null;
  radarActiveLayerRef: { current: 'layer1' | 'layer2' };
  radarCrossfadeTimeoutRef: { current: ReturnType<typeof setTimeout> | null };
  imageCacheRef: { current: Map<string, HTMLImageElement> };
  radarSourceId: string;
  radarLayerId: string;
  radarSourceId2: string;
  radarLayerId2: string;
  radarOpacity: number;
  fps: number;
  extendedTiles: RadarTile[];
  currentIndex: number;
  onRetry?: (retryFn: () => void) => void;
  abortSignal?: { aborted: boolean };
}

/** Safely check if a layer exists. Returns false if map/style is invalid (e.g. during rapid updates). */
function safeGetLayer(map: mapboxgl.Map, layerId: string): boolean {
  try {
    if (!map?.isStyleLoaded?.()) return false;
    return !!map.getLayer(layerId);
  } catch {
    return false;
  }
}

/**
 * Calculate radar bounding box coordinates from event data.
 * Used by both the main updateRadarLayer and the split map's direct-update path.
 */
export function getRadarBboxCoordinates(
  selectedEvent: EventWithCircuit,
): [[number, number], [number, number], [number, number], [number, number]] {
  if (selectedEvent.radar_bbox && selectedEvent.radar_bbox !== null && selectedEvent.radar_bbox.type === 'Polygon') {
    const polygonCoords = selectedEvent.radar_bbox.coordinates[0];
    let west = Infinity, east = -Infinity, south = Infinity, north = -Infinity;
    polygonCoords.forEach((coord: number[]) => {
      west = Math.min(west, coord[0]);
      east = Math.max(east, coord[0]);
      south = Math.min(south, coord[1]);
      north = Math.max(north, coord[1]);
    });
    return [[west, north], [east, north], [east, south], [west, south]];
  }
  const circuitLat = selectedEvent.circuit.latitude || 0;
  const circuitLon = selectedEvent.circuit.longitude || 0;
  const latOffset = 0.45;
  const lonOffset = 0.45;
  return [
    [circuitLon - lonOffset, circuitLat + latOffset],
    [circuitLon + lonOffset, circuitLat + latOffset],
    [circuitLon + lonOffset, circuitLat - latOffset],
    [circuitLon - lonOffset, circuitLat - latOffset],
  ];
}

/**
 * Updates the radar layer on the map with double-buffering for smooth playback.
 * Handles image preloading, crossfade transitions, and layer management.
 */
export function updateRadarLayer({
  map,
  currentTile,
  selectedEvent,
  radarActiveLayerRef,
  radarCrossfadeTimeoutRef,
  imageCacheRef,
  radarSourceId,
  radarLayerId,
  radarSourceId2,
  radarLayerId2,
  radarOpacity,
  fps,
  extendedTiles,
  currentIndex,
  onRetry,
  abortSignal,
}: UpdateRadarLayerParams): void {
  if (!map || !currentTile || !selectedEvent) return;

  // Check if style is loaded, retry if not
  if (!map.isStyleLoaded()) {
    if (onRetry) {
      onRetry(() => updateRadarLayer({
        map,
        currentTile,
        selectedEvent,
        radarActiveLayerRef,
        radarCrossfadeTimeoutRef,
        imageCacheRef,
        radarSourceId,
        radarLayerId,
        radarSourceId2,
        radarLayerId2,
        radarOpacity,
        fps,
        extendedTiles,
        currentIndex,
        onRetry,
        abortSignal,
      }));
    }
    return;
  }

  // Get radar bounding box from event (if available)
  let coordinates: [[number, number], [number, number], [number, number], [number, number]];

  if (selectedEvent.radar_bbox && selectedEvent.radar_bbox !== null && selectedEvent.radar_bbox.type === 'Polygon') {
    const polygonCoords = selectedEvent.radar_bbox.coordinates[0];

    let west = Infinity;
    let east = -Infinity;
    let south = Infinity;
    let north = -Infinity;

    polygonCoords.forEach((coord: number[]) => {
      const lon = coord[0];
      const lat = coord[1];
      west = Math.min(west, lon);
      east = Math.max(east, lon);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    });

    coordinates = [
      [west, north],  // top-left
      [east, north],  // top-right
      [east, south],  // bottom-right
      [west, south],  // bottom-left
    ];
  } else {
    const circuitLat = selectedEvent.circuit.latitude || 0;
    const circuitLon = selectedEvent.circuit.longitude || 0;
    const latOffset = 0.45;
    const lonOffset = 0.45;

    coordinates = [
      [circuitLon - lonOffset, circuitLat + latOffset], // top-left
      [circuitLon + lonOffset, circuitLat + latOffset], // top-right
      [circuitLon + lonOffset, circuitLat - latOffset], // bottom-right
      [circuitLon - lonOffset, circuitLat - latOffset], // bottom-left
    ];
  }

  // Determine which layer to use for the new frame (alternate between layer1 and layer2)
  const activeLayer = radarActiveLayerRef.current;
  const nextLayer = activeLayer === 'layer1' ? 'layer2' : 'layer1';
  const nextSourceId = nextLayer === 'layer1' ? radarSourceId : radarSourceId2;
  const nextLayerId = nextLayer === 'layer1' ? radarLayerId : radarLayerId2;
  const currentLayerId = activeLayer === 'layer1' ? radarLayerId : radarLayerId2;

  // Check if layers exist (use safe check - map style can be invalid during rapid updates)
  const layer1Exists = safeGetLayer(map, radarLayerId);
  const layer2Exists = safeGetLayer(map, radarLayerId2);

  // Helper function to create a layer with source
  const createLayerWithSource = (sourceId: string, layerId: string, url: string, opacity: number) => {
    try {
      if (!map.isStyleLoaded()) return;
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
          type: 'image',
          url,
          coordinates,
        });
      }
      if (!safeGetLayer(map, layerId)) {
        map.addLayer({
          id: layerId,
          type: 'raster',
          source: sourceId,
          paint: {
            'raster-opacity': opacity,
            'raster-fade-duration': 0, // Disable Mapbox fade, we handle crossfade manually
          },
        });
      }
    } catch {
      // Map/style invalid during rapid updates - exit silently
    }
  };

  // Helper function to update image source
  const updateImageSource = (sourceId: string, url: string) => {
    try {
      if (!map.isStyleLoaded()) return;
      const source = map.getSource(sourceId) as mapboxgl.ImageSource | undefined;
      if (source) {
        source.updateImage({ url, coordinates });
      }
    } catch {
      // Map/style invalid - exit silently
    }
  };

  // Calculate adaptive crossfade duration based on fps (capped at 5 fps for radar)
  // At high fps, use instant swap; at low fps, use smooth crossfade
  const cappedFps = Math.min(fps, 5); // Same cap as animation loop
  const frameDuration = 1000 / cappedFps;
  // Use 50% of frame duration for crossfade, max 100ms
  // At 5fps (200ms frame), this gives 100ms crossfade
  // At 1fps (1000ms frame), this gives 100ms crossfade (capped)
  const crossfadeDuration = Math.min(frameDuration * 0.5, 100);
  const useInstantSwap = crossfadeDuration < 50; // Below 50ms, just do instant swap (won't happen with 5fps cap)

  // Function to perform instant layer swap (for high fps)
  const instantSwap = () => {
    try {
      if (abortSignal?.aborted) return;
      if (!map.isStyleLoaded()) return;
      if (safeGetLayer(map, currentLayerId)) {
        map.setPaintProperty(currentLayerId, 'raster-opacity', 0);
      }
      if (safeGetLayer(map, nextLayerId)) {
        map.setPaintProperty(nextLayerId, 'raster-opacity', radarOpacity);
      }
      radarActiveLayerRef.current = nextLayer;
    } catch {
      // Map/style invalid during rapid updates - exit silently
    }
  };

  // Function to start the crossfade transition (for lower fps)
  const startCrossfade = () => {
    try {
      if (abortSignal?.aborted) return;
      if (!map.isStyleLoaded()) return;
    } catch {
      return;
    }

    const steps = Math.max(3, Math.floor(crossfadeDuration / 16)); // At least 3 steps, ~60fps max
    const stepDuration = crossfadeDuration / steps;
    let step = 0;

    const animateCrossfade = () => {
      if (abortSignal?.aborted) {
        radarCrossfadeTimeoutRef.current = null;
        return;
      }

      try {
        if (!map.isStyleLoaded()) return;
      } catch {
        radarCrossfadeTimeoutRef.current = null;
        return;
      }

      step++;
      const progress = step / steps;

      try {
        if (safeGetLayer(map, currentLayerId)) {
          map.setPaintProperty(currentLayerId, 'raster-opacity', radarOpacity * (1 - progress));
        }
        if (safeGetLayer(map, nextLayerId)) {
          map.setPaintProperty(nextLayerId, 'raster-opacity', radarOpacity * progress);
        }
      } catch {
        radarCrossfadeTimeoutRef.current = null;
        return;
      }

      if (step < steps) {
        radarCrossfadeTimeoutRef.current = setTimeout(animateCrossfade, stepDuration);
      } else {
        radarActiveLayerRef.current = nextLayer;
        radarCrossfadeTimeoutRef.current = null;
      }
    };

    animateCrossfade();
  };

  // Initialize both layers on first load
  if (!layer1Exists && !layer2Exists) {
    const cachedImg = imageCacheRef.current.get(currentTile.url);
    const imageReady = cachedImg && cachedImg.complete && cachedImg.naturalWidth > 0;

    if (imageReady) {
      createLayerWithSource(radarSourceId, radarLayerId, currentTile.url, radarOpacity);
      radarActiveLayerRef.current = 'layer1';
    } else {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (abortSignal?.aborted) return;
        imageCacheRef.current.set(currentTile.url, img);
        if (map.isStyleLoaded()) {
          createLayerWithSource(radarSourceId, radarLayerId, currentTile.url, radarOpacity);
          radarActiveLayerRef.current = 'layer1';
        }
      };
      img.onerror = () => {
        if (abortSignal?.aborted) return;
        console.warn(`Failed to load image: ${currentTile.url}`);
        if (map.isStyleLoaded()) {
          createLayerWithSource(radarSourceId, radarLayerId, currentTile.url, radarOpacity);
          radarActiveLayerRef.current = 'layer1';
        }
      };
      img.src = currentTile.url;
    }

    // Preload next frame
    if (currentIndex < extendedTiles.length - 1) {
      const nextTile = extendedTiles[currentIndex + 1];
      if (nextTile && !imageCacheRef.current.has(nextTile.url)) {
        const nextImg = new Image();
        nextImg.crossOrigin = 'anonymous';
        nextImg.onload = () => {
          imageCacheRef.current.set(nextTile.url, nextImg);
        };
        nextImg.src = nextTile.url;
      }
    }
    return;
  }

  // Check if image is already cached
  const cachedImg = imageCacheRef.current.get(currentTile.url);
  const imageReady = cachedImg && cachedImg.complete && cachedImg.naturalWidth > 0;

  // Prepare the next layer with the new image and transition
  const prepareAndTransition = () => {
    try {
      if (abortSignal?.aborted) return;
      if (!map.isStyleLoaded()) return;

      // Snap current active layer to full opacity before starting a new transition.
      // A previous crossfade may have been cancelled mid-way (e.g. rapid slider drag),
      // leaving the active layer at partial opacity.
      if (safeGetLayer(map, currentLayerId)) {
        map.setPaintProperty(currentLayerId, 'raster-opacity', radarOpacity);
      }

      // Create or update the next layer
      if (!map.getSource(nextSourceId)) {
        createLayerWithSource(nextSourceId, nextLayerId, currentTile.url, 0);
      } else {
        updateImageSource(nextSourceId, currentTile.url);
        if (!safeGetLayer(map, nextLayerId)) {
          map.addLayer({
            id: nextLayerId,
            type: 'raster',
            source: nextSourceId,
            paint: {
              'raster-opacity': 0,
              'raster-fade-duration': 0,
            },
          });
        } else {
          map.setPaintProperty(nextLayerId, 'raster-opacity', 0);
        }
      }

      // Use instant swap at high fps, crossfade at lower fps
      if (useInstantSwap) {
        radarCrossfadeTimeoutRef.current = setTimeout(() => {
          instantSwap();
          radarCrossfadeTimeoutRef.current = null;
        }, 8);
      } else {
        radarCrossfadeTimeoutRef.current = setTimeout(startCrossfade, 16);
      }
    } catch {
      // Map/style invalid during rapid updates - exit silently
    }
  };

  if (imageReady) {
    prepareAndTransition();
  } else {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (abortSignal?.aborted) return;
      imageCacheRef.current.set(currentTile.url, img);
      if (map.isStyleLoaded()) {
        prepareAndTransition();
      }
    };
    img.onerror = () => {
      if (abortSignal?.aborted) return;
      console.warn(`Failed to load image: ${currentTile.url}`);
      if (map.isStyleLoaded()) {
        prepareAndTransition();
      }
    };
    img.src = currentTile.url;
  }

  // Preload next frame for smoother playback
  if (currentIndex < extendedTiles.length - 1) {
    const nextTile = extendedTiles[currentIndex + 1];
    if (nextTile && !imageCacheRef.current.has(nextTile.url)) {
      const nextImg = new Image();
      nextImg.crossOrigin = 'anonymous';
      nextImg.onload = () => {
        imageCacheRef.current.set(nextTile.url, nextImg);
      };
      nextImg.src = nextTile.url;
    }
  }
}
